import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import AuthService from "../../services/auth.service";
import TokenManager from "../../services/TokenManager";

export const login = createAsyncThunk(
  "auth/login",
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const response = await AuthService.login(username, password);
      const token = response.data.access_token;
      TokenManager.storeCredentials(username, password);
      return token;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || "Failed to login");
    }
  }
);

export const fetchNewToken = createAsyncThunk(
  "auth/fetchNewToken",
  async (_, { rejectWithValue }) => {
    try {
      const newToken = await AuthService.fetchNewToken();
      return newToken;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || "Failed to fetch token"
      );
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    token: TokenManager.retrieve() || null,
    isAuthenticated: !!TokenManager.retrieve(),
    status: "idle",
    error: null,
    isFetchingToken: false,
  },
  reducers: {
    logout: (state) => {
      TokenManager.clear();
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    updateToken: (state, action) => {
      TokenManager.store(action.payload);
      state.token = action.payload;
      state.isAuthenticated = true;
    },
    clearError: (state) => {
      state.error = null;
    },
    initializeAuth: (state) => {
      const token = TokenManager.retrieve();
      if (token && TokenManager.isValid(token)) {
        state.token = token;
        state.isAuthenticated = true;
      } else {
        TokenManager.clear();
        state.token = null;
        state.isAuthenticated = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        TokenManager.store(action.payload);
        state.status = "succeeded";
        state.token = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to login";
        state.token = null;
        state.isAuthenticated = false;
      })
      .addCase(fetchNewToken.pending, (state) => {
        state.isFetchingToken = true;
        state.error = null;
      })
      .addCase(fetchNewToken.fulfilled, (state, action) => {
        TokenManager.store(action.payload);
        state.isFetchingToken = false;
        state.token = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(fetchNewToken.rejected, (state, action) => {
        state.isFetchingToken = false;
        state.error = action.payload || "Failed to fetch token";
        state.token = null;
        state.isAuthenticated = false;
        TokenManager.clear();
      });
  },
});

export const { logout, updateToken, clearError, initializeAuth } =
  authSlice.actions;
export default authSlice.reducer;
